import { useState, useEffect } from 'react';
import { uploadImage } from './api';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';

export default function CustomerCardManagement({ apiUrl }) {
  const { branches } = useBranch();
  const confirm = useConfirm();
  const [cards, setCards] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewCard, setPreviewCard] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    isActive: true,
    title: '',
    subtitle: '',
    icon: '🎉',
    cardType: 'promotion',
    linkedPromotionId: null,
    displayPosition: ['on-menu'],
    layout: 'compact',
    theme: {
      useDefault: true,
      customColors: {
        background: '#050505',
        border: 'rgba(249, 115, 22, 0.8)',
        text: '#ffffff'
      }
    },
    displayConditions: {
      dateRange: { startDate: '', endDate: '' },
      timeRange: { startTime: '', endTime: '' },
      daysOfWeek: [],
      tableTypes: [],
      zones: []
    },
    sortOrder: 0,
    content: {
      discount: { type: 'percentage', value: 0 },
      description: '',
      imageUrl: ''
    },
    branchIds: [] // ว่าง = ทุกสาขา (chain-wide), มีค่า = จำกัดเฉพาะสาขาที่เลือก
  });

  const [settings, setSettings] = useState({
    maxCardsDisplay: 2,
    cardsPerSlide: 2,
    autoSlideInterval: 5000,
    hideWhenEmpty: true,
    filterByCurrentTime: true
  });

  useEffect(() => {
    loadCards();
    loadPromotions();
    loadSettings();
  }, []);

  const loadCards = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/customer-cards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      setCards(result.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load cards:', err);
      setError('ไม่สามารถโหลดข้อมูลการ์ดได้');
    } finally {
      setLoading(false);
    }
  };

  const loadPromotions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/promotions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      setPromotions(result.data || []);
    } catch (err) {
      console.error('Failed to load promotions:', err);
    }
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/customer-cards/settings/global`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.data) {
        setSettings(result.data);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    const url = editingId
      ? `${apiUrl}/api/customer-cards/${editingId}`
      : `${apiUrl}/api/customer-cards`;
    const method = editingId ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          branchIds: formData.branchIds.length > 0 ? formData.branchIds : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'เกิดข้อผิดพลาด');
      }

      resetForm();
      await loadCards();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleEdit = (card) => {
    setEditingId(card._id);
    setFormData({
      ...card,
      linkedPromotionId: card.linkedPromotionId?._id || null,
      branchIds: (card.branchIds || []).map(id => String(id))
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!await confirm('คุณต้องการลบการ์ดนี้หรือไม่?', { title: 'ลบการ์ด', confirmText: 'ลบ', danger: true })) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${apiUrl}/api/customer-cards/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('ไม่สามารถลบการ์ดได้');
      await loadCards();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleToggle = async (id) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${apiUrl}/api/customer-cards/${id}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('ไม่สามารถเปลี่ยนสถานะได้');
      await loadCards();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleDuplicate = async (id) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${apiUrl}/api/customer-cards/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('ไม่สามารถคัดลอกการ์ดได้');
      await loadCards();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(`${apiUrl}/api/customer-cards/settings/global`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) throw new Error('ไม่สามารถบันทึกการตั้งค่าได้');

      setMessage({ type: 'success', text: 'บันทึกการตั้งค่าเรียบร้อย' });
      setShowSettings(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setShowForm(false);
    setFormData({
      isActive: true,
      title: '',
      subtitle: '',
      icon: '🎉',
      cardType: 'promotion',
      linkedPromotionId: null,
      displayPosition: ['on-menu'],
      layout: 'compact',
      theme: {
        useDefault: true,
        customColors: {
          background: '#050505',
          border: 'rgba(249, 115, 22, 0.8)',
          text: '#ffffff'
        }
      },
      displayConditions: {
        dateRange: { startDate: '', endDate: '' },
        timeRange: { startTime: '', endTime: '' },
        daysOfWeek: [],
        tableTypes: [],
        zones: []
      },
      sortOrder: 0,
      content: {
        discount: { type: 'percentage', value: 0 },
        description: '',
        imageUrl: ''
      },
      branchIds: []
    });
  };

  const toggleBranch = (branchId) => {
    const newIds = formData.branchIds.includes(branchId)
      ? formData.branchIds.filter(id => id !== branchId)
      : [...formData.branchIds, branchId];
    setFormData({ ...formData, branchIds: newIds });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'ไฟล์ใหญ่เกินไป! ขนาดไม่เกิน 5MB' });
      e.target.value = '';
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'รองรับเฉพาะไฟล์ JPG, PNG, WebP, GIF เท่านั้น' });
      e.target.value = '';
      return;
    }

    try {
      setUploading(true);
      const result = await uploadImage(file);
      setFormData({
        ...formData,
        content: {
          ...formData.content,
          imageUrl: result.url
        }
      });
      e.target.value = ''; // Clear file input
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'อัพโหลดไม่สำเร็จ กรุณาลองใหม่อีกครั้ง' });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="loading">กำลังโหลดข้อมูล...</div>;
  }

  return (
    <div className="customer-card-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>💳 จัดการการ์ดบนหน้าลูกค้า</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setShowSettings(true)} className="button button-secondary">
            ⚙️ ตั้งค่ารวม
          </button>
          <button onClick={() => setShowForm(true)} className="button button-primary">
            + สร้างการ์ดใหม่
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <Toast message={message} onDismiss={() => setMessage(null)} />

      {/* Cards Table */}
      <div className="card">
        <h3>รายการการ์ด ({cards.length})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table table-nowrap">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>เปิด/ปิด</th>
                <th>ชื่อการ์ด</th>
                <th>ประเภท</th>
                <th>โปรผูก</th>
                <th>ตำแหน่ง</th>
                <th>เลย์เอาต์</th>
                <th>สาขา</th>
                <th>ลำดับ</th>
                <th>สถานะ</th>
                <th style={{ width: '200px' }}>จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {cards.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '2rem', opacity: 0.6 }}>
                    ยังไม่มีการ์ด - คลิก "สร้างการ์ดใหม่" เพื่อเริ่มต้น
                  </td>
                </tr>
              ) : (
                cards.map(card => (
                  <tr key={card._id}>
                    <td>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={card.isActive}
                          onChange={() => handleToggle(card._id)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>{card.icon}</span>
                        <strong>{card.title}</strong>
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${card.cardType}`}>
                        {card.cardType === 'promotion' && '🎁 โปรโมชั่น'}
                        {card.cardType === 'announcement' && '📢 ประกาศ'}
                        {card.cardType === 'featured-menu' && '⭐ เมนูแนะนำ'}
                        {card.cardType === 'custom' && '🎨 กำหนดเอง'}
                      </span>
                    </td>
                    <td>
                      {card.linkedPromotionId ? (
                        <span style={{ fontSize: '0.85rem', color: '#10b981' }}>
                          ✓ {card.linkedPromotionId.name}
                        </span>
                      ) : (
                        <span style={{ opacity: 0.5 }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {card.displayPosition.map(pos => {
                        const labels = {
                          'on-menu': 'บนเมนู',
                          'home': 'หน้าแรก',
                          'above-categories': 'เหนือหมวด',
                          'below-promo': 'ใต้โปร',
                          'above-footer': 'เหนือ footer'
                        };
                        return labels[pos] || pos;
                      }).join(', ')}
                    </td>
                    <td>
                      <span style={{ fontSize: '0.85rem' }}>
                        {card.layout === 'two-cards-split' && '2 การ์ด'}
                        {card.layout === 'horizontal-40-60' && '40/60'}
                        {card.layout === 'compact' && 'Compact'}
                        {card.layout === 'full-width' && 'Full'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {!card.branchIds || card.branchIds.length === 0
                        ? '🌐 ทุกสาขา'
                        : card.branchIds.map(id => branches.find(b => b.id === String(id))?.name || '-').join(', ')}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{card.sortOrder}</td>
                    <td>
                      <span className={`status-badge ${card.isActive ? 'active' : 'inactive'}`}>
                        {card.isActive ? 'เปิด' : 'ปิด'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEdit(card)}
                          className="button button-sm button-warning"
                          title="แก้ไข"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => {
                            setPreviewCard(card);
                            setShowPreview(true);
                          }}
                          className="button button-sm button-info"
                          title="ดูตัวอย่าง"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => handleDuplicate(card._id)}
                          className="button button-sm button-secondary"
                          title="คัดลอก"
                        >
                          📋
                        </button>
                        <button
                          onClick={() => handleDelete(card._id)}
                          className="button button-sm button-danger"
                          title="ลบ"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals Container */}
      {/* Full Page Form Editor */}
      <Modal open={showForm} onClose={resetForm} maxWidth="1100px">
          {/* Header Bar */}
          <div style={{
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid rgba(251, 191, 36, 0.3)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ color: '#fbbf24', margin: 0 }}>
              {editingId ? '✏️ แก้ไขการ์ด' : '+ สร้างการ์ดใหม่'}
            </h2>
            <button
              onClick={resetForm}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              ✕ ปิด
            </button>
          </div>

          {/* 2 Column Layout: Form (left) + Preview (right) */}
          <div style={{
            display: 'flex',
            gap: '2rem',
            alignItems: 'flex-start'
          }}>
            {/* Left Column: Form (58%) */}
            <div style={{ flex: '0 0 58%' }}>
              <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div className="form-section">
                <h4>ข้อมูลพื้นฐาน</h4>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    {' '}แสดงบนหน้าลูกค้า
                  </label>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ flex: 3 }}>
                    <label>ชื่อการ์ด *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="เช่น โปรช่วงกลางวัน"
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>อีโมจิ/ไอคอน</label>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="🎉"
                      maxLength="2"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>รายละเอียดสั้น</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="ข้อความใต้หัวการ์ด"
                  />
                </div>

                <div className="form-group">
                  <label>รูปภาพประกอบ (Optional)</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      style={{ flex: 1 }}
                    />
                    {uploading && <span style={{ color: '#fbbf24' }}>🔄 กำลังอัพโหลด...</span>}
                    {formData.content.imageUrl && (
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          content: { ...formData.content, imageUrl: '' }
                        })}
                        className="button button-sm button-danger"
                      >
                        🗑️ ลบรูป
                      </button>
                    )}
                  </div>
                  {formData.content.imageUrl && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <img
                        src={formData.content.imageUrl}
                        alt="Preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '150px',
                          borderRadius: '8px',
                          border: '2px solid rgba(251, 191, 36, 0.3)',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>ประเภทการ์ด *</label>
                    <select
                      value={formData.cardType}
                      onChange={(e) => setFormData({ ...formData, cardType: e.target.value })}
                      required
                    >
                      <option value="promotion">🎁 โปรโมชั่น</option>
                      <option value="announcement">📢 ประกาศ</option>
                      <option value="featured-menu">⭐ เมนูแนะนำ</option>
                      <option value="custom">🎨 กำหนดเอง</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>ผูกกับโปรโมชั่น</label>
                    <select
                      value={formData.linkedPromotionId || ''}
                      onChange={(e) => setFormData({ ...formData, linkedPromotionId: e.target.value || null })}
                    >
                      <option value="">— ไม่ผูก —</option>
                      {promotions.map(promo => (
                        <option key={promo._id} value={promo._id}>
                          {promo.name} ({promo.type === 'percentage' ? `-${promo.value}%` : `-${promo.value}฿`})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>ตำแหน่งที่แสดง (เลือกได้หลายตำแหน่ง)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {['on-menu', 'home', 'above-categories', 'below-promo', 'above-footer'].map(pos => {
                      const labels = {
                        'on-menu': '📱 บนหน้าเมนู',
                        'home': '🏠 หน้าแรก',
                        'above-categories': '📂 เหนือหมวดหมู่',
                        'below-promo': '🎊 ใต้แบนเนอร์โปร',
                        'above-footer': '⬆️ เหนือ Footer'
                      };
                      return (
                        <label key={pos} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <input
                            type="checkbox"
                            checked={formData.displayPosition.includes(pos)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, displayPosition: [...formData.displayPosition, pos] });
                              } else {
                                setFormData({ ...formData, displayPosition: formData.displayPosition.filter(p => p !== pos) });
                              }
                            }}
                          />
                          <span style={{ fontSize: '0.9rem' }}>{labels[pos]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>เลย์เอาต์</label>
                    <select
                      value={formData.layout}
                      onChange={(e) => setFormData({ ...formData, layout: e.target.value })}
                    >
                      <option value="compact">📦 Compact (ประหยัดพื้นที่)</option>
                      <option value="two-cards-split">🎴 2 การ์ดเคียงกัน</option>
                      <option value="horizontal-40-60">↔️ แนวนอน 40/60</option>
                      <option value="full-width">⬛ Full Width</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>ลำดับการแสดง</label>
                    <input
                      type="number"
                      value={formData.sortOrder}
                      onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>สาขาที่แสดง</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, branchIds: [] })}
                      className={`button button-sm ${formData.branchIds.length === 0 ? 'button-primary' : 'button-secondary'}`}
                    >
                      🌐 ทุกสาขา
                    </button>
                    {branches.map(b => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => toggleBranch(b.id)}
                        className={`button button-sm ${formData.branchIds.includes(b.id) ? 'button-primary' : 'button-secondary'}`}
                      >
                        {b.name}
                      </button>
                    ))}
                  </div>
                  <small style={{ opacity: 0.6 }}>ไม่เลือกสาขาไหนเลย = แสดงทุกสาขาในเครือ</small>
                </div>
              </div>

              {/* Display Conditions */}
              <div className="form-section">
                <h4>เงื่อนไขการแสดง (Optional)</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label>วันที่เริ่ม</label>
                    <input
                      type="date"
                      value={formData.displayConditions.dateRange.startDate}
                      onChange={(e) => setFormData({
                        ...formData,
                        displayConditions: {
                          ...formData.displayConditions,
                          dateRange: { ...formData.displayConditions.dateRange, startDate: e.target.value }
                        }
                      })}
                    />
                  </div>

                  <div className="form-group">
                    <label>วันที่สิ้นสุด</label>
                    <input
                      type="date"
                      value={formData.displayConditions.dateRange.endDate}
                      onChange={(e) => setFormData({
                        ...formData,
                        displayConditions: {
                          ...formData.displayConditions,
                          dateRange: { ...formData.displayConditions.dateRange, endDate: e.target.value }
                        }
                      })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>เวลาเริ่ม (HH:MM)</label>
                    <input
                      type="time"
                      value={formData.displayConditions.timeRange.startTime}
                      onChange={(e) => setFormData({
                        ...formData,
                        displayConditions: {
                          ...formData.displayConditions,
                          timeRange: { ...formData.displayConditions.timeRange, startTime: e.target.value }
                        }
                      })}
                    />
                  </div>

                  <div className="form-group">
                    <label>เวลาสิ้นสุด (HH:MM)</label>
                    <input
                      type="time"
                      value={formData.displayConditions.timeRange.endTime}
                      onChange={(e) => setFormData({
                        ...formData,
                        displayConditions: {
                          ...formData.displayConditions,
                          timeRange: { ...formData.displayConditions.timeRange, endTime: e.target.value }
                        }
                      })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>วันในสัปดาห์ (เลือกได้หลายวัน)</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    {['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'].map((day, idx) => (
                      <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <input
                          type="checkbox"
                          checked={formData.displayConditions.daysOfWeek.includes(idx)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                displayConditions: {
                                  ...formData.displayConditions,
                                  daysOfWeek: [...formData.displayConditions.daysOfWeek, idx]
                                }
                              });
                            } else {
                              setFormData({
                                ...formData,
                                displayConditions: {
                                  ...formData.displayConditions,
                                  daysOfWeek: formData.displayConditions.daysOfWeek.filter(d => d !== idx)
                                }
                              });
                            }
                          }}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="button" onClick={resetForm} className="button button-secondary">
                  ✕ ยกเลิก
                </button>
                <button type="submit" className="button button-success">
                  ✓ บันทึก
                </button>
              </div>
            </form>
            </div>

            {/* Right Column: Sticky Preview (40%) */}
            <div style={{
                flex: '0 0 40%',
                position: 'sticky',
                top: '100px',
                height: 'calc(100vh - 120px)',
                overflowY: 'auto'
              }}>
                <div style={{
                  background: '#1e293b',
                  borderRadius: '16px',
                  padding: '1.5rem',
                  border: '2px solid rgba(251, 191, 36, 0.3)',
                  height: '100%'
                }}>
                  <h3 style={{
                    color: '#fbbf24',
                    marginBottom: '1.5rem',
                    textAlign: 'center',
                    fontSize: '1.2rem'
                  }}>
                    👁️ ตัวอย่างบนมือถือ
                  </h3>

                  {/* Mobile Mockup Frame */}
                  <div style={{
                    background: '#000',
                    borderRadius: '32px',
                    padding: '16px 12px',
                    border: '8px solid #334155',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                    maxWidth: '400px',
                    margin: '0 auto'
                  }}>
                    {/* Screen content area */}
                    <div style={{
                      background: '#0a0a0a',
                      borderRadius: '24px',
                      padding: '1.5rem 1rem',
                      minHeight: '550px',
                      maxHeight: 'calc(100vh - 350px)',
                      overflowY: 'auto'
                    }}>
                      <PreviewCustomerCard card={formData} />
                    </div>
                  </div>

                  <p style={{
                    fontSize: '0.85rem',
                    color: 'rgba(251, 191, 36, 0.6)',
                    textAlign: 'center',
                    marginTop: '1rem',
                    fontWeight: 500
                  }}>
                    ✨ อัพเดตแบบ real-time
                  </p>
                </div>
            </div>
          </div>
      </Modal>

      {/* Settings Modal */}
      <Modal open={showSettings} onClose={() => setShowSettings(false)} title="⚙️ ตั้งค่ารวม - โซนการ์ด" maxWidth="600px">
            <form onSubmit={handleSaveSettings}>
              <div className="form-group">
                <label>จำนวนการ์ดสูงสุดที่แสดง</label>
                <input
                  type="number"
                  value={settings.maxCardsDisplay}
                  onChange={(e) => setSettings({ ...settings, maxCardsDisplay: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="10"
                />
                <small>จำกัดการ์ดที่แสดงบนหน้าลูกค้า (1-10 การ์ด)</small>
              </div>

              <div className="form-group">
                <label>จำนวนการ์ดต่อสไลด์</label>
                <input
                  type="number"
                  value={settings.cardsPerSlide}
                  onChange={(e) => setSettings({ ...settings, cardsPerSlide: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="4"
                />
                <small>แสดงกี่การ์ดพร้อมกันในแต่ละสไลด์ (1-4)</small>
              </div>

              <div className="form-group">
                <label>เวลาเปลี่ยนสไลด์อัตโนมัติ (มิลลิวินาที)</label>
                <input
                  type="number"
                  value={settings.autoSlideInterval}
                  onChange={(e) => setSettings({ ...settings, autoSlideInterval: parseInt(e.target.value) || 3000 })}
                  min="1000"
                  max="30000"
                  step="500"
                />
                <small>ระยะเวลาก่อนเปลี่ยนสไลด์ (1000 = 1 วินาที)</small>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.hideWhenEmpty}
                    onChange={(e) => setSettings({ ...settings, hideWhenEmpty: e.target.checked })}
                  />
                  {' '}ซ่อนโซนการ์ดเมื่อไม่มีการ์ดที่เปิดใช้งาน
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.filterByCurrentTime}
                    onChange={(e) => setSettings({ ...settings, filterByCurrentTime: e.target.checked })}
                  />
                  {' '}แสดงเฉพาะการ์ดที่ตรงกับเวลาปัจจุบัน
                </label>
                <small>กรองตามวันที่และเวลาที่กำหนดในแต่ละการ์ด</small>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="button" onClick={() => setShowSettings(false)} className="button button-secondary">
                  ยกเลิก
                </button>
                <button type="submit" className="button button-success">
                  💾 บันทึก
                </button>
              </div>
            </form>
      </Modal>

      {/* Preview Modal */}
      <Modal open={showPreview && !!previewCard} onClose={() => setShowPreview(false)} maxWidth="500px">
            <h3 style={{ marginBottom: '1rem', color: '#fbbf24' }}>👁️ ตัวอย่างการ์ด</h3>

            <div style={{
              background: '#0a0a0a',
              padding: '1.5rem',
              borderRadius: '12px',
              minHeight: '200px'
            }}>
              <PreviewCustomerCard card={previewCard} />
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
              <button onClick={() => setShowPreview(false)} className="button button-secondary">
                ปิด
              </button>
            </div>
      </Modal>
    </div>
  );
}

// Preview Component - Mimics customer display
function PreviewCustomerCard({ card }) {
  // Format discount display
  const getDiscountText = () => {
    if (!card.content?.discount) return null;
    const { type, value } = card.content.discount;
    if (!value) return null;
    return type === 'percentage' ? `-${value}%` : `-${value}฿`;
  };

  // Format time range
  const getTimeRange = () => {
    const { startTime, endTime } = card.displayConditions?.timeRange || {};
    return startTime && endTime ? `${startTime}–${endTime}` : '';
  };

  // Format days of week
  const getDaysText = () => {
    const days = card.displayConditions?.daysOfWeek || [];
    if (days.length === 0) return '';

    const dayNames = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

    // Check for consecutive range
    if (days.length >= 5) {
      const sorted = [...days].sort((a, b) => a - b);
      const isConsecutive = sorted.every((day, i) =>
        i === 0 || day === sorted[i - 1] + 1
      );

      if (isConsecutive) {
        return `${dayNames[sorted[0]]}–${dayNames[sorted[sorted.length - 1]]}`;
      }
    }

    return days.map(d => dayNames[d]).join(', ');
  };

  const discountText = getDiscountText();
  const timeRange = getTimeRange();
  const daysText = getDaysText();

  // Render based on layout type
  if (card.layout === 'compact') {
    return (
      <div style={{
        position: 'relative',
        background: card.theme?.useDefault ? '#050505' : card.theme?.customColors?.background,
        border: `2px solid ${card.theme?.useDefault ? 'rgba(249, 115, 22, 0.8)' : card.theme?.customColors?.border}`,
        borderRadius: '20px',
        padding: '1rem',
        height: '100px',
        boxShadow: '0 8px 32px rgba(249, 115, 22, 0.4)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.4rem',
        overflow: 'hidden'
      }}>
        {/* Background Image */}
        {card.content?.imageUrl && (
          <img
            src={card.content.imageUrl}
            alt={card.title}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.25,
              borderRadius: '18px',
              zIndex: 0
            }}
          />
        )}

        {/* Content (overlay above image) */}
        <div style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}>
        {/* Discount Badge */}
        {discountText && (
          <div style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: '#FFC857',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>{card.icon}</span>
            {discountText}
          </div>
        )}

        {/* Card Title */}
        <div style={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'white',
          lineHeight: 1.2,
          textAlign: 'center'
        }}>
          {card.title}
        </div>

        {/* Subtitle */}
        {card.subtitle && (
          <div style={{
            fontSize: '0.85rem',
            color: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'center'
          }}>
            {card.subtitle}
          </div>
        )}

        {/* Time & Days */}
        {(timeRange || daysText) && (
          <div style={{
            fontSize: '0.85rem',
            color: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center'
          }}>
            {timeRange && <span>{timeRange}</span>}
            {timeRange && daysText && <span>|</span>}
            {daysText && <span>{daysText}</span>}
          </div>
        )}
        </div>
      </div>
    );
  }

  if (card.layout === 'two-cards-split') {
    return (
      <div style={{
        background: '#050505',
        border: '2px solid rgba(249, 115, 22, 0.8)',
        borderRadius: '20px',
        padding: '0.8rem',
        height: '120px',
        boxShadow: '0 8px 32px rgba(249, 115, 22, 0.4)',
        display: 'flex',
        gap: '1rem'
      }}>
        {/* Card 1 */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: '0.4rem',
          position: 'relative',
          padding: '0.5rem'
        }}>
          {discountText && (
            <div style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: '#FFC857',
              lineHeight: 1
            }}>
              {discountText}
            </div>
          )}
          <div style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'white',
            lineHeight: 1.2
          }}>
            {card.title}
          </div>
          {(timeRange || daysText) && (
            <div style={{
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              gap: '0.5rem'
            }}>
              {timeRange && <span>{timeRange}</span>}
              {timeRange && daysText && <span>|</span>}
              {daysText && <span>{daysText}</span>}
            </div>
          )}

          {/* Divider */}
          <div style={{
            position: 'absolute',
            right: 0,
            top: '10%',
            bottom: '10%',
            width: '1px',
            background: 'rgba(255, 255, 255, 0.2)'
          }} />
        </div>

        {/* Card 2 Placeholder */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255, 255, 255, 0.3)',
          fontSize: '0.85rem'
        }}>
          (การ์ดที่ 2)
        </div>
      </div>
    );
  }

  if (card.layout === 'horizontal-40-60') {
    return (
      <div style={{
        background: '#050505',
        border: '2px solid rgba(249, 115, 22, 0.8)',
        borderRadius: '20px',
        padding: '1rem',
        height: '120px',
        boxShadow: '0 8px 32px rgba(249, 115, 22, 0.4)',
        display: 'flex',
        gap: '1rem'
      }}>
        {/* Left 40% */}
        <div style={{
          flex: '0 0 40%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '0.3rem'
        }}>
          {discountText && (
            <div style={{
              fontSize: '1.8rem',
              fontWeight: 700,
              color: '#FFC857',
              lineHeight: 1
            }}>
              {discountText}
            </div>
          )}
          <div style={{
            fontSize: '0.95rem',
            fontWeight: 600,
            color: 'white'
          }}>
            {card.title}
          </div>
        </div>

        {/* Right 60% */}
        <div style={{
          flex: '0 0 60%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '0.3rem'
        }}>
          {card.subtitle && (
            <div style={{
              fontSize: '0.9rem',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              {card.subtitle}
            </div>
          )}
          {(timeRange || daysText) && (
            <div style={{
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              gap: '0.5rem'
            }}>
              {timeRange && <span>{timeRange}</span>}
              {timeRange && daysText && <span>|</span>}
              {daysText && <span>{daysText}</span>}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (card.layout === 'full-width') {
    return (
      <div style={{
        background: '#050505',
        border: '2px solid rgba(249, 115, 22, 0.8)',
        borderRadius: '20px',
        padding: '1.5rem',
        minHeight: '150px',
        boxShadow: '0 8px 32px rgba(249, 115, 22, 0.4)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.8rem'
      }}>
        {/* Top: Icon + Discount */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <span style={{ fontSize: '3rem' }}>{card.icon}</span>
          {discountText && (
            <div style={{
              fontSize: '2.5rem',
              fontWeight: 700,
              color: '#FFC857',
              lineHeight: 1
            }}>
              {discountText}
            </div>
          )}
        </div>

        {/* Middle: Title + Subtitle */}
        <div>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: 'white',
            marginBottom: '0.3rem'
          }}>
            {card.title}
          </div>
          {card.subtitle && (
            <div style={{
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>
              {card.subtitle}
            </div>
          )}
        </div>

        {/* Bottom: Time & Days */}
        {(timeRange || daysText) && (
          <div style={{
            fontSize: '0.9rem',
            color: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            gap: '0.5rem'
          }}>
            {timeRange && <span>⏰ {timeRange}</span>}
            {timeRange && daysText && <span>|</span>}
            {daysText && <span>📅 {daysText}</span>}
          </div>
        )}
      </div>
    );
  }

  return null;
}
